import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { hasFeature as checkFeature } from "../utils/featureFlags";

/**
 * Custom hook to access feature flags from Redux store
 *
 * @returns {object} Feature flags utilities
 * @returns {object} return.featureFlags - All feature flags
 * @returns {function} return.hasFeature - Check if a specific feature is enabled
 * @returns {boolean} return.isLoading - Whether feature flags are being loaded
 *
 * @example
 * const { featureFlags, hasFeature } = useFeatureFlags();
 *
 * if (hasFeature('proposals')) {
 *   // Show proposals feature
 * }
 */
export function useFeatureFlags() {
  // Get feature flags from Redux store
  const featureFlags = useSelector((state) => state.auth.featureFlags);
  const token = useSelector((state) => state.auth.token);

  const { normalizedFeatureFlags, enabledFeatures, disabledFeatures } = useMemo(() => {
    const flags = featureFlags ?? {};

    const enabled = [];
    const disabled = [];

    Object.entries(flags).forEach(([key, value]) => {
      if (value === true) {
        enabled.push(key);
      } else if (value === false) {
        disabled.push(key);
      }
    });

    return {
      normalizedFeatureFlags: flags,
      enabledFeatures: enabled,
      disabledFeatures: disabled,
    };
  }, [featureFlags]);

  return {
    featureFlags: normalizedFeatureFlags,

    enabledFeatures,

    disabledFeatures,

    hasFeature: (featureName) => {
      return checkFeature(normalizedFeatureFlags, featureName);
    },

    isLoading: !token || !featureFlags,
  };
}
